import { isPlatformBrowser } from "@angular/common";
import { HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest, HttpResponse } from "@angular/common/http";
import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { environment } from "@env/environment";
import { EncryptedResponse } from "@shared/api-obfuscation";
import { from, Observable } from "rxjs";
import { mergeMap } from "rxjs/operators";

import { EncryptionService } from "./encryption.service";
import { RouteMappingService } from "./route-mapping.service";
import { SsrAuthService } from "./ssr-auth.service";

@Injectable()
export class ApiObfuscationInterceptor implements HttpInterceptor {
  private ssrAuth: SsrAuthService | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private encryption: EncryptionService,
    private routeMapping: RouteMappingService
  ) {
    // Initialize SSR auth service only in SSR environment
    if (!isPlatformBrowser(this.platformId)) {
      this.ssrAuth = new SsrAuthService();
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // In SSR environment, handle ALL requests with SSR auth only
    // Skip obfuscation entirely as responses won't be encrypted
    if (!isPlatformBrowser(this.platformId)) {
      if (this.ssrAuth) {
        return from(this.handleSsrRequest(req)).pipe(mergeMap(ssrReq => next.handle(ssrReq)));
      }
      // If no SSR auth service, just pass through
      return next.handle(req);
    }

    // Browser mode - normal obfuscation handling
    if (!this.routeMapping.isObfuscatedRoute(req.url)) {
      // Log non-obfuscated requests in non-production environments
      if (!environment.production) {
        console.log(`[ApiObfuscation] Not obfuscating ${req.method} request to: ${req.url}`);
        if (req.method === "GET" && req.params.keys().length > 0) {
          const params: any = {};
          req.params.keys().forEach(key => {
            params[key] = req.params.getAll(key);
          });
          console.log(`[ApiObfuscation] Query params:`, params);
        } else if (req.method === "POST" || req.method === "PUT" || req.method === "PATCH") {
          console.log(`[ApiObfuscation] Request body:`, req.body);
        }
      }
      return next.handle(req);
    }

    return from(this.handleObfuscatedRequest(req)).pipe(
      mergeMap(encryptedReq => next.handle(encryptedReq)),
      mergeMap(event => this.handleObfuscatedResponse(event))
    );
  }

  private async handleSsrRequest(req: HttpRequest<any>): Promise<HttpRequest<any>> {
    if (!this.ssrAuth) {
      return req;
    }

    // Generate signature for this request
    // Extract just the path and query params (no protocol/host)
    const url = new URL(req.urlWithParams, "http://dummy.com");
    const pathWithParams = url.pathname + url.search;

    const { signature, timeBlock, timestamp } = await this.ssrAuth.generateSignature(
      req.method,
      pathWithParams,
      req.body
    );

    // Add SSR authentication headers
    return req.clone({
      setHeaders: {
        "X-SSR-Signature": signature,
        "X-SSR-TimeBlock": timeBlock.toString(),
        "X-SSR-Timestamp": timestamp.toString()
      }
    });
  }

  private async handleObfuscatedRequest(req: HttpRequest<any>): Promise<HttpRequest<any>> {
    // Log request details in non-production environments
    if (!environment.production) {
      console.log(`[ApiObfuscation] Intercepting ${req.method} request to: ${req.url}`);

      if (req.method === "GET") {
        if (req.params.keys().length > 0) {
          const params: any = {};
          req.params.keys().forEach(key => {
            params[key] = req.params.getAll(key);
          });
          console.log(`[ApiObfuscation] Query params (from HttpParams):`, params);
        } else if (req.url.includes("?")) {
          // Parse params from URL string
          const queryStringIndex = req.url.indexOf("?");
          const queryString = req.url.substring(queryStringIndex + 1);
          console.log(`[ApiObfuscation] Query params (from URL):`, queryString);
        }
      } else if (req.method === "POST" || req.method === "PUT" || req.method === "PATCH") {
        console.log(`[ApiObfuscation] Request body:`, req.body);
      }
    }

    // Get the base URL without query parameters
    const urlWithoutParams = req.url.split("?")[0];

    // Log extracted URL parameters for pattern-based routes (non-production only)
    if (!environment.production && (req.method === "POST" || req.method === "PUT" || req.method === "PATCH")) {
      const extractedParams = this.routeMapping.extractUrlParams(urlWithoutParams);
      if (extractedParams && Object.keys(extractedParams.params).length > 0) {
        console.log(`[ApiObfuscation] Extracted URL params:`, extractedParams.params);
        console.log(`[ApiObfuscation] Target UUID:`, extractedParams.uuid);
      }
    }

    // Check if this is a pattern-based route
    const extractedParams = this.routeMapping.extractUrlParams(urlWithoutParams);
    const isPatternRoute = extractedParams && Object.keys(extractedParams.params).length > 0;

    const newUrl = this.routeMapping.replaceWithUuid(urlWithoutParams);
    let modifiedReq = req.clone({ url: newUrl });

    // Skip encryption for HEAD requests only
    if (req.method === "HEAD") {
      return modifiedReq;
    }

    if (req.method === "GET") {
      if (isPatternRoute && extractedParams) {
        // For pattern-based routes, encrypt URL params separately
        const urlParamsEncrypted = await this.encryption.encryptRequest(extractedParams.params);

        // Start with URL params encryption
        let encryptedOnlyParams = new HttpParams()
          .set("p", urlParamsEncrypted.data)
          .set("pn", urlParamsEncrypted.nonce)
          .set("t", urlParamsEncrypted.t.toString());

        // Check if there are query parameters to encrypt
        const queryParams: { [key: string]: string | string[] } = {};

        // Check if we have params in HttpParams object
        if (req.params.keys().length > 0) {
          // Use params from HttpParams
          req.params.keys().forEach(key => {
            const values = req.params.getAll(key);
            if (values.length === 1) {
              queryParams[key] = values[0];
            } else {
              queryParams[key] = values;
            }
          });
        } else if (req.url.includes("?")) {
          // URL has query string but HttpParams is empty
          // This is normal when the URL is constructed with embedded parameters

          // Parse parameters from the URL
          const queryStringIndex = req.url.indexOf("?");
          if (queryStringIndex > -1) {
            const queryString = req.url.substring(queryStringIndex + 1);
            const params = new HttpParams({ fromString: queryString });

            params.keys().forEach(key => {
              const values = params.getAll(key);
              if (values.length === 1) {
                queryParams[key] = values[0];
              } else {
                queryParams[key] = values;
              }
            });
          }
        }

        // If we have query params, encrypt them
        if (Object.keys(queryParams).length > 0) {
          const queryParamsEncrypted = await this.encryption.encryptRequest(queryParams);
          encryptedOnlyParams = encryptedOnlyParams
            .set("d", queryParamsEncrypted.data)
            .set("n", queryParamsEncrypted.nonce)
            .set("encrypted", "true");
        }

        modifiedReq = modifiedReq.clone({
          params: encryptedOnlyParams,
          body: null
        });
      } else {
        // For static routes, extract and encrypt all query parameters together
        const queryParams: { [key: string]: string | string[] } = {};

        // Check if we have params in HttpParams object
        if (req.params.keys().length > 0) {
          // Use params from HttpParams
          req.params.keys().forEach(key => {
            const values = req.params.getAll(key);
            if (values.length === 1) {
              queryParams[key] = values[0];
            } else {
              queryParams[key] = values;
            }
          });
        } else if (req.url.includes("?")) {
          // URL has query string but HttpParams is empty
          // This is normal when the URL is constructed with embedded parameters

          // Parse parameters from the URL
          const queryStringIndex = req.url.indexOf("?");
          if (queryStringIndex > -1) {
            const queryString = req.url.substring(queryStringIndex + 1);
            const params = new HttpParams({ fromString: queryString });

            params.keys().forEach(key => {
              const values = params.getAll(key);
              if (values.length === 1) {
                queryParams[key] = values[0];
              } else {
                queryParams[key] = values;
              }
            });
          }
        }

        const encryptedParams = await this.encryption.encryptRequest(queryParams);

        // Replace all original parameters with only encrypted ones
        const encryptedOnlyParams = new HttpParams()
          .set("encrypted", "true")
          .set("d", encryptedParams.data)
          .set("n", encryptedParams.nonce)
          .set("t", encryptedParams.t.toString());

        modifiedReq = modifiedReq.clone({
          params: encryptedOnlyParams,
          body: null
        });
      }
    } else if (req.body || isPatternRoute) {
      // For other methods with body, include URL params if pattern-based
      let bodyToEncrypt = req.body || {};

      if (isPatternRoute && extractedParams) {
        // Include URL params in the body for pattern-based routes
        bodyToEncrypt = {
          params: extractedParams.params,
          ...bodyToEncrypt
        };

        // Log what we're about to encrypt for pattern-based routes
        if (!environment.production) {
          console.log(`[ApiObfuscation] Pattern-based route detected`);
          console.log(`[ApiObfuscation] URL params being added to body:`, extractedParams.params);
          console.log(`[ApiObfuscation] Full body to encrypt:`, bodyToEncrypt);
        }
      }

      const encryptedBody = await this.encryption.encryptRequest(bodyToEncrypt);

      if (!environment.production) {
        console.log(`[ApiObfuscation] Encrypted body:`, encryptedBody);
      }

      modifiedReq = modifiedReq.clone({
        body: encryptedBody,
        setHeaders: {
          "Content-Type": "application/json"
        }
      });
    }

    return modifiedReq;
  }

  private async handleObfuscatedResponse(event: HttpEvent<any>): Promise<HttpEvent<any>> {
    if (!(event instanceof HttpResponse)) {
      return event;
    }

    const response = event as HttpResponse<any>;

    if (!response.body) {
      return response;
    }

    const encryptedData = this.extractEncryptedData(response.body);

    if (!encryptedData) {
      return response;
    }

    try {
      const decryptedBody = await this.encryption.decryptResponse(encryptedData);

      // Debug logging for development
      if (!environment.production) {
        console.log(`[ApiObfuscation] Response from: ${response.url}`);
        console.log(`[ApiObfuscation] Decrypted response:`, decryptedBody);
      }

      return response.clone({ body: decryptedBody });
    } catch (error) {
      console.error("Failed to decrypt response", error);
      throw error;
    }
  }

  private extractEncryptedData(body: any): EncryptedResponse | null {
    if (this.isEncryptedResponse(body)) {
      return body;
    }

    if (this.isEncryptedGetResponse(body)) {
      return {
        data: body.d,
        encrypted: true,
        nonce: body.n,
        t: typeof body.t === "string" ? parseInt(body.t, 10) : body.t
      };
    }

    return null;
  }

  private isEncryptedResponse(body: any): body is EncryptedResponse {
    return (
      body &&
      typeof body === "object" &&
      body.encrypted === true &&
      typeof body.data === "string" &&
      typeof body.nonce === "string"
    );
  }

  private isEncryptedGetResponse(body: any): boolean {
    // For GET responses, check if the response has the shortened parameter names
    return (
      body &&
      typeof body === "object" &&
      typeof body.d === "string" &&
      typeof body.n === "string" &&
      (typeof body.t === "string" || typeof body.t === "number")
    );
  }
}
